import type { OttoBot, OttoBotWithSecret } from "./types.js";

/**
 * Typed fetch wrappers for the dashboard's /api/otto/bots proxy.
 * Each helper threads through the Authorization header from
 * the calling component (the dashboard's existing auth context).
 */
async function request<T>(
  path: string,
  init: RequestInit = {},
  authHeader?: string,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (authHeader) headers.set("Authorization", authHeader);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`/api/otto${path}`, { ...init, headers });
  const text = await res.text();
  if (!res.ok) {
    let body: unknown;
    try { body = JSON.parse(text); } catch { body = text; }
    throw new Error(
      typeof body === "object" && body !== null && "error" in body
        ? String((body as { error: unknown }).error)
        : `Request failed: ${res.status}`,
    );
  }
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const ottoBotsApi = {
  list: (auth?: string) => request<OttoBot[]>("/bots", {}, auth),
  get:  (id: number, auth?: string) => request<OttoBot>(`/bots/${id}`, {}, auth),
  create: (payload: Partial<OttoBot>, auth?: string) =>
    request<OttoBotWithSecret>("/bots", { method: "POST", body: JSON.stringify(payload) }, auth),
  update: (id: number, payload: Partial<OttoBot>, auth?: string) =>
    request<OttoBot>(`/bots/${id}`, { method: "PATCH", body: JSON.stringify(payload) }, auth),
  rotateSecret: (id: number, auth?: string) =>
    request<OttoBotWithSecret>(`/bots/${id}/rotate-secret`, { method: "POST" }, auth),
  rotateEmbedKey: (id: number, auth?: string) =>
    request<OttoBot>(`/bots/${id}/rotate-embed-key`, { method: "POST" }, auth),
  remove: (id: number, auth?: string) =>
    request<void>(`/bots/${id}`, { method: "DELETE" }, auth),
};
